"use client";

import type { ReactNode } from "react";

import { Icon } from "@/components/ui/icon";
import { isRunning, useSearchRunsStore } from "@/stores/search-runs-store";
import { cn } from "@/lib/utils";

/** Spinner glyph shared by the sidebar rows and the phase pill. */
export function RunningDot({
  className,
  ...props
}: {
  className?: string;
  "data-testid"?: string;
}) {
  return (
    <Icon
      name="IconLoadingCircle"
      size={12}
      className={cn("animate-spin text-heading opacity-70", className)}
      {...props}
    />
  );
}

/**
 * Sidebar / list indicator for one saved search, read straight from the runs
 * store (hydrated by the list and detail GETs, then kept live by the project
 * SSE stream): a spinner while a run is searching or ranking, a red dot for a
 * failed run and a primary dot for a READY run the user has not opened yet.
 * Renders `fallback` (the row's identity swatch) when there is nothing to
 * flag, so the row keeps its layout either way.
 */
export function RunIndicatorDot({
  searchId,
  fallback = null,
}: {
  searchId: string;
  fallback?: ReactNode;
}) {
  const run = useSearchRunsStore((s) => s.runs[searchId]);
  const seen = useSearchRunsStore((s) => s.seenReady[searchId]);
  if (!run) return fallback;
  if (isRunning(run.phase)) {
    return <RunningDot data-testid="run-indicator-running" />;
  }
  if (run.phase === "failed") {
    return (
      <span
        className="size-1.5 rounded-full bg-destructive"
        data-testid="run-indicator-failed"
      />
    );
  }
  if (
    run.phase === "ready" &&
    run.latestReadyRunId &&
    seen !== run.latestReadyRunId
  ) {
    return (
      <span
        // Ring keeps the dot legible on the blue identity swatches.
        className="size-1.5 rounded-full bg-primary ring-2 ring-surface-primary"
        data-testid="run-indicator-unseen"
      />
    );
  }
  return fallback;
}

/**
 * Header / toolbar pill naming the live phase of a search's latest run.
 * Renders nothing once the run has stopped.
 */
export function RunPhasePill({
  searchId,
  className,
}: {
  searchId: string;
  className?: string;
}) {
  const run = useSearchRunsStore((s) => s.runs[searchId]);
  if (!run || !isRunning(run.phase)) return null;
  const label = run.phase === "searching" ? "Searching" : "Ranking";
  return (
    <span
      role="status"
      data-testid="run-phase-pill"
      className={cn(
        "inline-flex h-5 shrink-0 items-center gap-1 rounded-md bg-contrast/6 px-1.5 text-xs text-foreground",
        className,
      )}
    >
      <Icon name="IconLoadingCircle" size={10} className="animate-spin" />
      {label}
    </span>
  );
}
